// ...existing code...
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { createCustomStateSetOnUrlHandler, getAllWithParams, getOne, sortItems, StateDictionary } from '@appState/helpers';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, pipe } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import {
  CategorizedNewsArticlesRequestParams,
  CategorizedNewsArticlesResponse,
  CommentRequest,
  NewsArticle,
  NewsArticleCommentListItem,
  NewsArticleFilterParams,
  NewsArticleListItem,
  NewsTopicsPreference,
  NewsTopicsPreferenceUpdate,
  Reaction,
  ReactRequest,
} from './news-article.model';
import { NewsArticlesService } from './news-articles.service';

export type NewsArticlesState = {
  filteredNewsArticles: NewsArticleListItem[];
  isFilteredArticlesLoading: boolean;

  promotedNewsArticles: NewsArticleListItem[];
  isPromotedArticlesLoading: boolean;

  categorizedNewsArticles: CategorizedNewsArticlesResponse[];
  isCategorizedArticlesLoading: boolean;

  openedNewsArticle: NewsArticle | null;
  isOpenedNewsArticleLoading: boolean;

  topicsPreferences: NewsTopicsPreference[];
  isTopicsPreferencesLoading: boolean;
  isTopicsPreferencesUpdateLoading: boolean;

  comments: NewsArticleCommentListItem[];
  isCommentsLoading: boolean;
  isCommentLoading: boolean;
  isReactionLoading: boolean;
  isImagesLoading: boolean;
  isCreateCommentInProgress: boolean;

  viewTypeToggle: 'categorized' | 'full';
};

const initialState: NewsArticlesState = {
  filteredNewsArticles: [],
  isFilteredArticlesLoading: false,

  promotedNewsArticles: [],
  isPromotedArticlesLoading: false,

  categorizedNewsArticles: [],
  isCategorizedArticlesLoading: false,

  openedNewsArticle: null,
  isOpenedNewsArticleLoading: false,

  topicsPreferences: [],
  isTopicsPreferencesLoading: false,
  isTopicsPreferencesUpdateLoading: false,

  comments: [],
  isCommentsLoading: false,
  isCommentLoading: false,
  isReactionLoading: false,
  isImagesLoading: false,
  isCreateCommentInProgress: false,

  viewTypeToggle: 'full',
};

const newsArticleDictionary: StateDictionary<NewsArticle> = {
  listLoading: 'isFilteredArticlesLoading',
  entityList: 'filteredNewsArticles',
  createLoading: '',
  selectedEntity: 'openedNewsArticle',
  selectedEntityLoading: 'isOpenedNewsArticleLoading',
  entityId: 'groupId',
};

const filteredNewsArticleListDictionary: StateDictionary<NewsArticleListItem> = {
  listLoading: 'isFilteredArticlesLoading',
  entityList: 'filteredNewsArticles',
  createLoading: '',
  selectedEntity: 'openedNewsArticle',
  selectedEntityLoading: 'isOpenedNewsArticleLoading',
  entityId: 'id',
};

const promotedNewsArticleListDictionary: StateDictionary<NewsArticleListItem> = {
  ...filteredNewsArticleListDictionary,
  entityList: 'promotedNewsArticles',
  listLoading: 'isPromotedArticlesLoading',
};

export const latestNewsArticleSortFunc = () => {
  return (a: NewsArticleListItem, b: NewsArticleListItem) => (getUsableArticleDate(a) < getUsableArticleDate(b) ? 1 : -1);
};

export const getUsableArticleDate = (newsArticle: NewsArticleListItem) => {
  const communityApprovedDate = newsArticle.communities[0]!.approvedDateTime;

  return communityApprovedDate < newsArticle.validFromDate! ? newsArticle.validFromDate! : communityApprovedDate;
};

export const sortByDateTimeComment = (a: NewsArticleCommentListItem, b: NewsArticleCommentListItem) =>
  a.createdDateTime < b.createdDateTime ? 1 : -1;

export const sortByDateTimeCommentsRecursively = (comments: NewsArticleCommentListItem[]): NewsArticleCommentListItem[] => {
  return comments
    .map(comment => ({
      ...comment,
      comments: sortByDateTimeCommentsRecursively(comment.comments),
    }))
    .sort(sortByDateTimeComment);
};

export const findAndRemoveComment = (
  comments: NewsArticleCommentListItem[],
  predicate: (comment: NewsArticleCommentListItem) => boolean,
): NewsArticleCommentListItem[] => {
  return comments.reduce((acc, comment) => {
    if (!predicate(comment)) {
      const newComment = { ...comment };

      if (newComment.comments) {
        newComment.comments = findAndRemoveComment(newComment.comments, predicate);
      }

      acc.push(newComment);
    }
    return acc;
  }, [] as NewsArticleCommentListItem[]);
};

export const addComment = (
  comments: NewsArticleCommentListItem[],
  newComment: NewsArticleCommentListItem,
): NewsArticleCommentListItem[] => {
  if (!newComment.parentCommentId) {
    return sortItems([...comments, newComment], sortByDateTimeComment);
  }

  return comments.map(comment => {
    if (comment.id === newComment.parentCommentId) {
      const updatedChildren = sortItems([...(comment.comments || []), newComment], sortByDateTimeComment);
      return {
        ...comment,
        comments: updatedChildren,
      };
    } else if (comment.comments && comment.comments.length > 0) {
      return {
        ...comment,
        comments: addComment(comment.comments, newComment),
      };
    } else {
      return comment;
    }
  });
};

export const updateComment = (
  comments: NewsArticleCommentListItem[],
  updatedComment: NewsArticleCommentListItem,
): NewsArticleCommentListItem[] => {
  return comments.map(comment => {
    if (comment.id === updatedComment.id) {
      return {
        ...comment,
        ...updatedComment,
        userName: comment.userName,
        comments: comment.comments,
      };
    } else if (comment.comments && comment.comments.length > 0) {
      return {
        ...comment,
        comments: updateComment(comment.comments, updatedComment),
      };
    } else {
      return comment;
    }
  });
};

export const NewsArticlesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('news-articles-store'),
  withComputed(state => ({
    sortedFilteredNewsArticles: computed(() => sortItems(state.filteredNewsArticles(), latestNewsArticleSortFunc())),
    sortedPromotedNewsArticles: computed(() => sortItems(state.promotedNewsArticles(), latestNewsArticleSortFunc())),
  })),
  withMethods((store, newsArticlesService = inject(NewsArticlesService)) => {
    createCustomStateSetOnUrlHandler<NewsArticlesState>('/news-articles', { openedNewsArticle: null, comments: [], topicsPreferences: [] })(
      store,
    );

    const repositoryMethods = {
      getAllFiltered: getAllWithParams<NewsArticleListItem, NewsArticleFilterParams>({
        dictionary: filteredNewsArticleListDictionary,
        store,
        service: newsArticlesService,
        onSuccessCallback(entities) {
          patchState(store, {
            filteredNewsArticles: sortItems(entities, latestNewsArticleSortFunc()),
          });
        },
      }),

      getAllPromoted: getAllWithParams<NewsArticleListItem, NewsArticleFilterParams>({
        dictionary: promotedNewsArticleListDictionary,
        store,
        service: newsArticlesService,
        onSuccessCallback(entities) {
          patchState(store, {
            promotedNewsArticles: sortItems(entities, latestNewsArticleSortFunc()),
          });
        },
      }),

      getCategorized: rxMethod<CategorizedNewsArticlesRequestParams>(
        pipe(
          tap(() => patchState(store, { isCategorizedArticlesLoading: true })),
          switchMap(filters =>
            newsArticlesService.getCategorized(filters).pipe(
              tap(categorizedArticles =>
                patchState(store, {
                  categorizedNewsArticles: categorizedArticles,
                  isCategorizedArticlesLoading: false,
                }),
              ),
              catchError(() => {
                patchState(store, { isCategorizedArticlesLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      getOne: getOne<NewsArticle>({
        dictionary: newsArticleDictionary,
        store,
        service: newsArticlesService,
      }),

      getTopicsPreferences: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { isTopicsPreferencesLoading: true })),
          switchMap(() =>
            newsArticlesService.getTopicsPreferences().pipe(
              tap(topicsPreferences => patchState(store, { topicsPreferences, isTopicsPreferencesLoading: false })),
              catchError(() => {
                patchState(store, { isTopicsPreferencesLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      updateTopicsPreferences: rxMethod<NewsTopicsPreferenceUpdate>(
        pipe(
          tap(() => patchState(store, { isTopicsPreferencesUpdateLoading: true })),
          switchMap(topicsPreferences =>
            newsArticlesService.updateTopicsPreferences(topicsPreferences).pipe(
              tap(topicsPreferences => {
                patchState(store, { topicsPreferences, isTopicsPreferencesUpdateLoading: false });
                repositoryMethods.getCategorized({ countPerTopic: 5 });
              }),
              catchError(() => {
                patchState(store, { isTopicsPreferencesUpdateLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      reactOnNewsArticle: rxMethod<{ articleId: string; req: ReactRequest }>(
        pipe(
          tap(({ req }) => {
            const opened = store.openedNewsArticle();
            if (opened) {
              patchState(store, {
                openedNewsArticle: {
                  ...opened,
                  myReaction: req.reaction,
                  communityStatistics: [
                    {
                      ...opened.communityStatistics[0],
                      likesCount:
                        req.reaction === 'Like'
                          ? opened.communityStatistics[0].likesCount + 1
                          : opened.communityStatistics[0].likesCount - 1,
                    },
                  ],
                },
                isReactionLoading: true,
              });
            }
          }),

          switchMap(({ articleId, req }) =>
            newsArticlesService.reactOnArticle(articleId, req).pipe(
              tap(() =>
                patchState(store, {
                  isReactionLoading: false,
                }),
              ),
              catchError(() => {
                const opened = store.openedNewsArticle();
                if (opened) {
                  patchState(store, {
                    openedNewsArticle: {
                      ...opened,
                      myReaction: opened.myReaction === 'None' ? 'Like' : ('None' as Reaction),
                      communityStatistics: [
                        {
                          ...opened.communityStatistics[0],
                          likesCount:
                            opened.myReaction === 'None'
                              ? opened.communityStatistics[0].likesCount + 1
                              : opened.communityStatistics[0].likesCount - 1,
                        },
                      ],
                    },
                    isReactionLoading: false,
                  });
                }
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      getAllNewsArticleComments: rxMethod<{ articleId: string }>(
        pipe(
          tap(() => patchState(store, { isCommentsLoading: true })),
          switchMap(({ articleId }) =>
            newsArticlesService.getAllComments(articleId).pipe(
              tap(comments =>
                patchState(store, {
                  isCommentsLoading: false,
                  comments: sortByDateTimeCommentsRecursively(comments),
                }),
              ),
              catchError(() => {
                patchState(store, { isCommentsLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      createNewsArticleComment: rxMethod<{ articleId: string; req: CommentRequest }>(
        pipe(
          tap(() => patchState(store, { isCommentLoading: true, isCommentsLoading: true, isCreateCommentInProgress: true })),
          switchMap(({ articleId, req }) =>
            newsArticlesService.createComment(articleId, req).pipe(
              tap(comment =>
                patchState(store, {
                  comments: addComment(store.comments(), comment),
                  isCommentLoading: false,
                  isCommentsLoading: false,
                  isCreateCommentInProgress: false,
                }),
              ),
              catchError(() => {
                patchState(store, { isCommentLoading: false, isCommentsLoading: false, isCreateCommentInProgress: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      reactOnNewsArticleComment: rxMethod<{ commentWithNewReaction: NewsArticleCommentListItem }>(
        pipe(
          tap(({ commentWithNewReaction }) =>
            patchState(store, {
              comments: updateComment(store.comments(), commentWithNewReaction),
              isReactionLoading: true,
            }),
          ),
          switchMap(({ commentWithNewReaction }) =>
            newsArticlesService
              .reactOnComment(commentWithNewReaction.newsArticleId, commentWithNewReaction.id, {
                reaction: commentWithNewReaction.myReaction,
              })
              .pipe(
                tap(() =>
                  patchState(store, {
                    isReactionLoading: false,
                  }),
                ),
                catchError(() => {
                  patchState(store, {
                    comments: updateComment(store.comments(), {
                      ...commentWithNewReaction,
                      myReaction: commentWithNewReaction.myReaction === 'None' ? 'Like' : 'None',
                      likesCount:
                        commentWithNewReaction.myReaction === 'None'
                          ? commentWithNewReaction.likesCount + 1
                          : commentWithNewReaction.likesCount - 1,
                    }),
                    isReactionLoading: false,
                  });
                  return EMPTY;
                }),
              ),
          ),
        ),
      ),
    };

    return {
      ...repositoryMethods,

      setViewTypeToggle(viewType: 'categorized' | 'full') {
        patchState(store, { viewTypeToggle: viewType });
      },
      resetListState() {
        patchState(store, {
          filteredNewsArticles: [],
          isFilteredArticlesLoading: false,
          promotedNewsArticles: [],
          isPromotedArticlesLoading: false,
        });
      },
    };
  }),
);

export interface NewsArticlesStoreType {
  readonly filteredNewsArticles: Signal<NewsArticleListItem[]>;
  readonly isFilteredArticlesLoading: Signal<boolean>;

  readonly promotedNewsArticles: Signal<NewsArticleListItem[]>;
  readonly isPromotedArticlesLoading: Signal<boolean>;

  readonly categorizedNewsArticles: Signal<CategorizedNewsArticlesResponse[]>;
  readonly isCategorizedArticlesLoading: Signal<boolean>;

  readonly openedNewsArticle: Signal<NewsArticle | null>;
  readonly isOpenedNewsArticleLoading: Signal<boolean>;

  readonly topicsPreferences: Signal<NewsTopicsPreference[]>;
  readonly isTopicsPreferencesLoading: Signal<boolean>;
  readonly isTopicsPreferencesUpdateLoading: Signal<boolean>;

  readonly comments: Signal<NewsArticleCommentListItem[]>;
  readonly isCommentsLoading: Signal<boolean>;
  readonly isCommentLoading: Signal<boolean>;
  readonly isCreateCommentInProgress: Signal<boolean>;
  readonly isReactionLoading: Signal<boolean>;
  readonly isImagesLoading: Signal<boolean>;
  readonly viewTypeToggle: Signal<'categorized' | 'full'>;

  readonly sortedFilteredNewsArticles: Signal<NewsArticleListItem[]>;
  readonly sortedPromotedNewsArticles: Signal<NewsArticleListItem[]>;

  readonly getAllFiltered: (filters: NewsArticleFilterParams) => void;
  readonly getAllPromoted: (filters: NewsArticleFilterParams) => void;
  readonly getCategorized: (filters: CategorizedNewsArticlesRequestParams) => void;
  readonly getOne: (groupId: string) => void;
  readonly reactOnNewsArticle: (params: { articleId: string; req: ReactRequest }) => void;
  readonly getAllNewsArticleComments: (params: { articleId: string }) => void;
  readonly createNewsArticleComment: (params: { articleId: string; req: CommentRequest }) => void;
  readonly reactOnNewsArticleComment: (params: { commentWithNewReaction: NewsArticleCommentListItem }) => void;
  readonly setViewTypeToggle: (viewType: 'categorized' | 'full') => void;
  readonly resetListState: () => void;
  readonly getTopicsPreferences: () => void;
  readonly updateTopicsPreferences: (topicsPreferences: NewsTopicsPreferenceUpdate) => void;
}
